import { useEffect, useState } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';

interface Licence {
  active: boolean;
  expiresAt?: string | null;
  schoolName?: string;
  daysRemaining?: number;
}

/**
 * SaaS licence notice. Expiry doesn't lock a teacher out — they keep their data
 * — so the wall has to be announced before they hit it, not discovered when a
 * grading click fails.
 */
export function LicenceBanner() {
  const { user } = useAuth();
  const [licence, setLicence] = useState<Licence | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const res = await fetch('/api/assignments/licence', {
          headers: { 'x-teacher-id': user.id, Authorization: `Bearer ${data?.session?.access_token ?? ''}` },
        });
        if (res.ok && !cancelled) setLicence(await res.json());
      } catch {
        // a banner is not worth surfacing an error for
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!licence || licence.expiresAt == null) return null;
  const when = new Date(licence.expiresAt).toLocaleDateString('zh-HK');

  if (!licence.active) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>使用期限已屆滿</AlertTitle>
        {licence.schoolName ?? '貴校'}的使用期限已於 {when} 屆滿。您仍可查看已有的作業與批改記錄，
        但暫時無法新增作業或批改。請聯絡管理員續期。
      </Alert>
    );
  }
  if ((licence.daysRemaining ?? 999) <= 30) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {licence.schoolName ?? '貴校'}的使用期限將於 {when} 屆滿（尚餘 {licence.daysRemaining} 天），請及早聯絡管理員續期。
      </Alert>
    );
  }
  return null;
}
